<div class="container">
  <div class="row">
    <div class="col">
      <div
        id="urgent-gaps"
        class="callout__container pt-0 text-dark"
        style="--callout-accent-color: #06ded8"
      >
        <div class="callout__img position-relative">
          <div class="actor-text position-absolute text-white d-none d-lg-block">
            Actor Portrayal
          </div>
          <picture
            [data]="{
              image: 't1d-family-history/urgent-gap',
              webp: true,
              altext: 'A father and his daughter sitting on a couch',
              format: '',
              width: '1190',
              height: '500',
              mobile: true,
              classes: 'img-fluid',
            }"
          ></picture>
        </div>
        <h2 class="mb-4">Urgent Gaps in Risk Awareness</h2>
        <div class="callout--mini d-flex flex-column mb-4">
          <h2 class="mb-0 text-center text-lg-start text-black mt-0">
            Patients with just one immediate family member with type 1 diabetes
            <span class="text-primary fst-italic">~15x more likely to develop T1D</span>
            themselves.<sup>1</sup>
          </h2>
        </div>
        <p class="mb-3">
          Despite their elevated risk, individuals with a first-degree family
          history of T1D are not receiving the information they need about their
          risk or available screening options. These knowledge gaps matter.
          <span class="fw-bold"
            >Up to 62% of unscreened individuals are diagnosed during diabetic
            ketoacidosis (DKA), a potentially life-threatening
            complication.</span
          ><sup>3</sup>
        </p>
        <p class="mb-3">
          A new survey conducted by Beyond Type 1 and commissioned by Sanofi
          among adults and caregivers of children with a first-degree family
          member with T1D reveals important opportunities for healthcare
          providers to strengthen conversations around family history, screening
          and early risk detection.<sup>2</sup>
        </p>
        <p class="mb-0">
          Initiating conversations about elevated family risk before symptoms
          develop gives patients the information they need to consider screening
          earlier.
        </p>
      </div>

      <div
        id="family-risk"
        class="callout__container text-dark"
        style="--callout-accent-color: #9657d6"
      >
        <h2 class="mb-4">The Family Factor: A T1D Risk Awareness Survey</h2>
        <p class="mb-3">
          The 2026 survey of 1,000 US adults included two at-risk audiences:
          adults ages 18–45 with a first-degree family member living with type 1
          diabetes (T1D), and caregivers ages 18–60 of children under age 18
          with a first-degree family member living with T1D
          <span class="text-primary"
            >(see bottom of page for full methodology)</span
          >.<sup>2</sup>
        </p>
        <p class="mb-3">
          The findings highlight important gaps that healthcare providers can
          help address. Even among people with a first-degree family history of
          T1D, understanding of disease risk and screening remained limited.<sup>2</sup>
        </p>
        <div class="callout__banners d-flex flex-column">
          <div class="banner d-flex flex-column flex-lg-row align-items-center">
            <img
              src="/assets/img/t1d-family-history/heart-people.svg"
              width="159"
              height="159"
              alt="A family of three"
            />
            <h3 class="mb-0 text-center text-lg-start">
              31%
              <span class="fw-normal">of those surveyed are unaware</span> that
              family history can increase a person's risk of developing T1D<sup
                >2</sup
              >
            </h3>
          </div>
          <div class="banner d-flex flex-column flex-lg-row align-items-center">
            <img
              src="/assets/img/t1d-family-history/cells.svg"
              width="159"
              height="159"
              alt="A cluster of cells"
            />
            <h3 class="mb-0 text-center text-lg-start">
              34% <span class="fw-normal">of those surveyed are</span> unaware
              that T1D is caused by an autoimmune attack
              <span class="fw-normal"
                >on insulin producing beta cells<sup>2</sup></span
              >
            </h3>
          </div>
          <div class="banner d-flex flex-column flex-lg-row align-items-center">
            <img
              src="/assets/img/t1d-family-history/people.svg"
              width="159"
              height="159"
              alt="Two people and an exclamation point"
            />
            <h3 class="mb-0 text-center text-lg-start">
              <span class="fw-normal"
                >While 82% of respondents expressed concern about their or their
                child's risk of developing T1D,</span
              >
              46%
              <span class="fw-normal">of respondents said they were</span>
              either not considering autoantibody screening or had not decided
              if they would within the next six months.<sup>2</sup>
            </h3>
          </div>
          <div class="banner d-flex flex-column flex-lg-row align-items-center">
            <img
              src="/assets/img/t1d-family-history/heart-magnifying-glass.svg"
              width="159"
              height="159"
              alt="A magnifying glass looking at a heart"
            />
            <h3 class="mb-0 text-center text-lg-start">
              28% <span class="fw-normal">of respondents</span> had never heard
              of screening<sup>2</sup>
            </h3>
          </div>
        </div>

        <h2 class="text-primary mt-4 mt-lg-3 mb-0">
          Opportunity:
          <span class="fw-normal"
            >Patients may not recognize the implications of their own family
            history. Proactively discussing risk and autoantibody screening can
            help bridge that gap.</span
          >
        </h2>
      </div>

      <div
        id="misconceptions"
        class="callout__container pt-0"
        style="--callout-accent-color: #ff5e2b"
      >
        <div class="callout__img position-relative">
          <div class="actor-text position-absolute text-white">
            Actor Portrayal
          </div>
          <picture
            [data]="{
              image: 't1d-family-history/misconceptions',
              webp: true,
              altext: 'A mother and father sitting on the couch with their son',
              format: '',
              width: '1190',
              height: '500',
              mobile: true,
              classes: 'img-fluid',
            }"
          ></picture>
        </div>
        <h2 class="mb-4 text-dark">
          Persistent Misconceptions about T1D Screening
        </h2>
        <p class="mb-3 text-dark">
          Beyond lack of familial risk awareness and familiarity or confidence
          in screening, some survey respondents also reported significant
          misconceptions about T1D as a disease, as well as about accessing T1D
          screening.
        </p>
        <p class="mb-3 text-dark">
          These misconceptions may prevent patients from realizing they are at
          elevated risk and from seeking screening to begin with. Addressing
          these misunderstandings during routine conversations can help remove
          barriers to action.
        </p>
        <div class="callout__banners d-flex flex-column">
          <div class="banner d-flex flex-column flex-lg-row align-items-center">
            <img
              src="/assets/img/t1d-family-history/magnifying-glass-people.svg"
              width="159"
              height="159"
              alt="A magnifying glass looking at a family of three"
            />
            <h3 class="mb-0 text-center text-lg-start">
              63% of respondents
              <span class="fw-normal"
                >think the disease is primarily diagnosed in children.<sup
                  >2</sup
                ></span
              >
            </h3>
          </div>
          <div class="banner d-flex flex-column flex-lg-row align-items-center">
            <img
              src="/assets/img/t1d-family-history/chart-icon.svg"
              width="159"
              height="159"
              alt="A rising bar graph"
            />
            <h3 class="mb-0 text-center text-lg-start">
              40% of respondents
              <span class="fw-normal"
                >think living with obesity increases a person’s risk of T1D.<sup
                  >2</sup
                ></span
              >
            </h3>
          </div>
          <div class="banner d-flex flex-column flex-lg-row align-items-center">
            <img
              src="/assets/img/t1d-family-history/doctor-icon.svg"
              width="159"
              height="159"
              alt="A healthcare provider and a speech bubble"
            />
            <h3 class="mb-0 text-center text-lg-start">
              31% of respondents
              <span class="fw-normal"
                >think autoantibody screening is only available through
                specialists.<sup>2</sup></span
              >
            </h3>
          </div>
          <div class="banner d-flex flex-column flex-lg-row align-items-center">
            <img
              src="/assets/img/t1d-family-history/magnifying-glass.svg"
              width="159"
              height="159"
              alt="A magnifying glass looking at an exclamation point"
            />
            <h3 class="mb-0 text-center text-lg-start">
              26% of respondents
              <span class="fw-normal"
                >think autoantibody screening is only possible after symptoms
                appear.<sup>2</sup></span
              >
            </h3>
          </div>
        </div>

        <h2 class="text-primary mt-3 mb-0">
          Opportunity:
          <span class="fw-normal"
            >Autoantibody screening can be initiated in primary care; addressing
            these misunderstandings during routine conversations can help remove
            barriers to action.
          </span>
        </h2>
        <div class="callout__btn--1 mt-3">
          <div
            class="action-plan__btn"
            global-button
            [buttonData]="cardButton"
          ></div>
        </div>
      </div>

      <div
        id="discussing-screening"
        class="callout__container pt-0"
        style="--callout-accent-color: #06ded8"
      >
        <div class="callout__img position-relative">
          <div class="actor-text position-absolute text-white">
            Actor Portrayal
          </div>
          <picture
            [data]="{
              image: 't1d-family-history/discussing-screening',
              webp: true,
              altext: 'A mother and daughter with their doctor',
              format: '',
              width: '1190',
              height: '500',
              mobile: true,
              classes: 'img-fluid',
            }"
          ></picture>
        </div>
        <h2 class="mb-4 text-dark">Discussing Screening with your Patients</h2>
        <p class="mb-3 text-dark">
          A conversation with a healthcare provider can make the difference
          between awareness and action. Among respondents who were hesitant
          about screening, a doctor's recommendation was the strongest motivator
          to consider screening.<sup>2</sup>
        </p>
        <div class="callout__chart">
          <picture
            [data]="{
              image: 't1d-family-history/discussing-screening-chart',
              webp: true,
              altext: '',
              format: '',
              width: '922',
              height: '321',
              mobile: false,
              classes: 'img-fluid',
            }"
          ></picture>
        </div>
        <div class="callout--mini d-flex flex-column mb-4">
          <h2 class="mb-0 text-center text-lg-start text-black mt-0">
            And yet, while
            <span class="text-primary fst-italic">62% of respondents</span> said their
            doctor has discussed their T1D risk with them,
            <span class="text-primary fst-italic">only 28% of respondents</span> familiar
            with screening
            <span class="text-primary fst-italic"
              >reported their doctor initiated a discussion</span
            >
            with them about it.<sup>2</sup>
          </h2>
        </div>
        <h2 class="text-primary mt-3 mb-0">
          Opportunity:
          <span class="fw-normal"
            >Conversations about family history and T1D risk are not
            consistently leading to conversations about screening. Closing that
            gap could help more at-risk patients understand their options before
            symptoms develop and potentially avoid severe medical emergencies.
          </span>
        </h2>
        <div class="callout__btn--2 mt-3">
          <div
            class="action-plan__btn"
            global-button
            [buttonData]="cardButton2"
          ></div>
        </div>
      </div>
    </div>
  </div>
</div>
